// frontend/src/contexts/SessionGuardContext.tsx
import React, { createContext, useContext, useRef, useCallback, useEffect, useState } from 'react';
import type { FormInstance } from 'antd';

export type GuardedForm = {
  id: string;
  isDirty: () => boolean;
  stash: () => void;
  restore: () => boolean;
  validateAndSave?: () => Promise<{ saved: boolean; recordId?: number | string }>;
};

const STASH_PREFIX = 'cnf_stash:';

export function readStash(stashKey: string): any | null {
  const raw = localStorage.getItem(STASH_PREFIX + stashKey);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
export function writeStash(stashKey: string, value: any): void {
  try { localStorage.setItem(STASH_PREFIX + stashKey, JSON.stringify(value)); } catch { /* quota */ }
}
export function clearStash(stashKey: string): void {
  localStorage.removeItem(STASH_PREFIX + stashKey);
}
export function listStashKeys(): string[] {
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(STASH_PREFIX)) out.push(k.slice(STASH_PREFIX.length));
  }
  return out;
}

type GuardApi = { register: (f: GuardedForm) => () => void; getForms: () => GuardedForm[] };
const Ctx = createContext<GuardApi | null>(null);

export function SessionGuardProvider({ children }: { children: React.ReactNode }) {
  const forms = useRef<Map<string, GuardedForm>>(new Map());
  const [, forceUpdate] = useState(0);
  const register = useCallback((f: GuardedForm) => {
    forms.current.set(f.id, f);
    forceUpdate(n => n + 1);
    return () => {
      forms.current.delete(f.id);
      forceUpdate(n => n + 1);
    };
  }, []);
  const getForms = useCallback(() => Array.from(forms.current.values()), []);
  return <Ctx.Provider value={{ register, getForms }}>{children}</Ctx.Provider>;
}

export function useSessionGuard(): GuardApi {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Return no-ops when rendered outside the provider (e.g. isolated unit tests).
    // In production all routes are wrapped by SessionGuardProvider in App.tsx.
    return { register: () => () => {}, getForms: () => [] };
  }
  return ctx;
}

/** Tier A: any antd form opts into local stash + restore.
 *  isDirty = "form holds any non-empty value" — a cheap blanket net. Precise
 *  user-edit tracking lives in the Tier B editors (via the public onValuesChange). */
export function useSessionStashedForm(form: FormInstance, stashKey: string): void {
  const { register } = useSessionGuard();
  useEffect(() => {
    const guarded: GuardedForm = {
      id: 'stash:' + stashKey,
      isDirty: () => {
        const v = form.getFieldsValue(true);
        return !!v && Object.values(v).some(
          x => x !== undefined && x !== null && x !== '' &&
               !(Array.isArray(x) && x.length === 0),
        );
      },
      stash: () => writeStash(stashKey, { values: form.getFieldsValue(true), route: window.location.pathname }),
      restore: () => {
        const v = readStash(stashKey);
        if (!v?.values) return false;
        form.setFieldsValue(v.values);
        return true;
      },
    };
    return register(guarded);
  }, [register, form, stashKey]);

  // When the user opted to restore (RestorePrompt armed it), re-apply this form's
  // stash on mount, then clear it and disarm so other forms don't double-consume.
  useEffect(() => {
    if (sessionStorage.getItem('cnf_restore_armed') !== '1') return;
    const v = readStash(stashKey);
    if (!v?.values) return;
    form.setFieldsValue(v.values);
    clearStash(stashKey);
    sessionStorage.removeItem('cnf_restore_armed');
  }, [form, stashKey]);
}

/** Tier B: an editor registers a validated server save plus a stash fallback. */
export function useGuardedEditor(
  id: string,
  opts: {
    isDirty: () => boolean;
    validateAndSave: () => Promise<{ saved: boolean; recordId?: number | string }>;
    stash: () => void;
    restore: () => boolean;
  },
): void {
  const { register } = useSessionGuard();
  const ref = React.useRef(opts);
  ref.current = opts;
  useEffect(() => {
    const guarded: GuardedForm = {
      id,
      isDirty: () => ref.current.isDirty(),
      stash: () => ref.current.stash(),
      restore: () => ref.current.restore(),
      validateAndSave: () => ref.current.validateAndSave(),
    };
    return register(guarded);
  }, [register, id]);

  useEffect(() => {
    if (sessionStorage.getItem('cnf_restore_armed') !== '1') return;
    if (!readStash(id)) return;
    ref.current.restore();
    clearStash(id);
    sessionStorage.removeItem('cnf_restore_armed');
  }, [id]);
}
